/**
 * useDemoRunLog.ts
 *
 * The demo's RUN LOG (plan §4.7's last bullet, §4.9 tab 5; card D8b): every
 * fired event, every refused press and every warning, in order, exportable — for
 * the demo that goes wrong in front of a customer.
 *
 * WHAT FEEDS IT, honestly stated, because the hub's half is not all there yet:
 *
 *  1. **Every player press this app makes** — play / pause / speed / seek / day /
 *     cut / replayCut — recorded by `useDemoClock` the moment the hub answers
 *     (or refuses: a refusal is a WARNING with the hub's own words). This is the
 *     part a demo operator needs most, and it is the part that exists today.
 *  2. **`demo:event`** — the hub's own run-log line (plan §3.4, `{at, kind,
 *     label}`). The hub publishes the moments IT can vouch for — a named cut the
 *     clock crossed while playing, and a day rollover (`DemoMoments`, card
 *     `t_89e24d6b`) — through `useDemoFeed`, so every client reads the same
 *     beats. The script's routines/scenes/camera moments join that feed when a
 *     runner executes them; until then nothing claims they fired.
 *  3. **Day, mode and script transitions** read off `demo:state`/`demo:clock`
 *     frames — a day rollover is the line an operator scans for after a run.
 *
 * The log is a module-level ring (the newest `DEMO_RUN_LOG_LIMIT` lines), so a
 * player surface that mounts mid-run shows the run so far instead of an empty
 * list, and closing the settings page does not throw the evidence away. It is
 * deliberately NOT persisted: a reload starts a new session's evidence, and a
 * stale log that claims a replay happened yesterday is worse than none.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue';

import { tt } from '../utils/consoleText';

/** How many lines the ring keeps — enough for a six-day run's operator passes. */
export const DEMO_RUN_LOG_LIMIT = 300;

/** What a line is: a verb, a hub event, or a warning. */
export type DemoRunLogKind =
  | 'play'
  | 'pause'
  | 'speed'
  | 'seek'
  | 'day'
  | 'cut'
  | 'replayCut'
  | 'mode'
  | 'event'
  | 'warning';

/** One line of the log. */
export interface DemoRunLogEntry {
  /** A monotonic id (a `v-for` key that survives identical lines). */
  id: number;
  /** When this app saw it (real ms). */
  at: number;
  /** The sim instant it belongs to, when the caller had one (UTC ISO, ''). */
  simIso: string;
  kind: DemoRunLogKind;
  /** `seek 08:05`, `Replay cut morning`, the hub's own sentence for a refusal. */
  label: string;
  /** The raw payload, for the export (never rendered). */
  payload?: unknown;
}

const entries: Ref<DemoRunLogEntry[]> = ref([]);
let nextId = 1;

function truncate(value: unknown, limit = 400): string {
  const text = typeof value === 'string' ? value : value === null || value === undefined ? '' : String(value);
  return text.length > limit ? `${text.slice(0, limit)}…` : text;
}

/**
 * One readable line out of a `demo:event` payload (plan §3.4: `{at, kind,
 * label}` — the hub's own moment, see the hub's `DemoMoments`).
 *
 * The hub's label is BILINGUAL (`{en, sl}`) because the label is the show's own
 * words, so it is picked with the app's locale like every other line in this
 * file — a run log that answered English in Slovene would read as a foreign
 * record of a Slovene demo. The kind is the hub's short word (`cut`, `day`) and
 * is localised here; a kind this build does not know is shown as sent.
 */
const HUB_EVENT_KINDS: Record<string, [string, string]> = {
  cut: ['cut', 'izrez'],
  day: ['day', 'dan'],
};

function eventLabel(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return truncate(payload);
  const shaped = payload as {
    at?: unknown;
    kind?: unknown;
    label?: unknown;
    message?: unknown;
    viewpoint?: unknown;
  };
  const rawLabel = shaped.label ?? shaped.message ?? '';
  const bilingual =
    rawLabel !== null &&
    typeof rawLabel === 'object' &&
    typeof (rawLabel as { en?: unknown }).en === 'string' &&
    typeof (rawLabel as { sl?: unknown }).sl === 'string'
      ? (rawLabel as { en: string; sl: string })
      : null;
  const label = bilingual ? tt(bilingual.en, bilingual.sl) : truncate(rawLabel);
  const rawKind = truncate(shaped.kind ?? '');
  const pair = HUB_EVENT_KINDS[rawKind.toLowerCase()];
  const kind = pair ? tt(pair[0], pair[1]) : rawKind;
  const at = truncate(shaped.at ?? '');
  const viewpoint = truncate(shaped.viewpoint ?? '');
  if (label === '' && kind === '') return truncate(JSON.stringify(payload));
  return [at, kind, label, viewpoint].filter((part) => part !== '').join(' · ');
}

/**
 * Push one line. `pushDemoRunLog({kind:'event', payload})` is the socket shape;
 * `pushDemoRunLog({kind:'seek', label:'08:05', simIso})` the player's.
 */
export function pushDemoRunLog(input: {
  kind: DemoRunLogKind;
  label?: string;
  simIso?: string;
  payload?: unknown;
}): void {
  const kind = input.kind;
  const label =
    typeof input.label === 'string' && input.label.trim() !== ''
      ? input.label.trim()
      : kind === 'event'
        ? eventLabel(input.payload)
        : kind;
  const entry: DemoRunLogEntry = {
    id: nextId,
    at: Date.now(),
    simIso: input.simIso ?? '',
    kind,
    label,
  };
  if (input.payload !== undefined) entry.payload = input.payload;
  nextId += 1;
  const next = [...entries.value, entry];
  entries.value = next.length > DEMO_RUN_LOG_LIMIT ? next.slice(next.length - DEMO_RUN_LOG_LIMIT) : next;
}

/** A refusal is a warning line, in the hub's own words (`demoErrorText`). */
export function pushDemoRunLogWarning(message: string, simIso = ''): void {
  pushDemoRunLog({ kind: 'warning', label: message, simIso });
}

/** Empty the log (the tab's own button; a fresh run starts with a clean page). */
export function clearDemoRunLog(): void {
  entries.value = [];
}

/** `HH:MM:SS` in the reader's own clock, for the export and the rows. */
function realClockLabel(at: number): string {
  const date = new Date(at);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

/** The whole log as copyable text (the export the plan asks for). */
export function demoRunLogText(): string {
  return entries.value
    .map((entry) => {
      const when = realClockLabel(entry.at);
      const sim = entry.simIso ? ` [${entry.simIso}]` : '';
      return `${when}${sim} ${entry.kind}: ${entry.label}`;
    })
    .join('\n');
}

/** The newest-first view a surface paints, with the real-clock label per row. */
export interface DemoRunLogView {
  entries: ComputedRef<DemoRunLogEntry[]>;
  /** Newest first — what the tab lists. */
  newestFirst: ComputedRef<DemoRunLogEntry[]>;
  /** How many warnings the run has produced (the tab's badge). */
  warningCount: ComputedRef<number>;
  clear: () => void;
  exportText: () => string;
  timeLabel: (entry: DemoRunLogEntry) => string;
  kindLabel: (kind: DemoRunLogKind) => string;
}

export function useDemoRunLog(): DemoRunLogView {
  return {
    entries: computed(() => entries.value),
    newestFirst: computed(() => [...entries.value].reverse()),
    warningCount: computed(() => entries.value.filter((entry) => entry.kind === 'warning').length),
    clear: clearDemoRunLog,
    exportText: demoRunLogText,
    timeLabel: (entry) => realClockLabel(entry.at),
    kindLabel: (kind) => {
      switch (kind) {
        case 'play':
          return tt('play', 'predvajaj');
        case 'pause':
          return tt('pause', 'premor');
        case 'speed':
          return tt('speed', 'hitrost');
        case 'seek':
          return tt('seek', 'skok');
        case 'day':
          return tt('day', 'dan');
        case 'cut':
          return tt('cut', 'izrez');
        case 'replayCut':
          return tt('replay cut', 'ponovi izrez');
        case 'mode':
          return tt('mode', 'način');
        case 'warning':
          return tt('warning', 'opozorilo');
        default:
          return tt('event', 'dogodek');
      }
    },
  };
}
